import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../../Components/Sidebar";
import Topbar from "../../Components/Topbar";
import {
  listConversations,
  getConversationById,
  getConversationMessages,
  setActiveAssistant,
} from "../../actions/assistants";
import { RobotIcon } from "../Auth/onboarding/icons";
import { ChevronDownIcon } from "../Dashboard/icons";
import { ExportIcon } from "./icons";
import { mapConversation, mapConversations } from "./mapConversation";
import FilterTabs from "./FilterTabs";
import SourceTabs from "./SourceTabs";
import ConversationList from "./ConversationList";
import ConversationDetail from "./ConversationDetail";
import LeadInfoPanel from "./LeadInfoPanel";
import useOwnerInbox from "./useOwnerInbox";
import LoadingState from "../../Components/LoadingState";

// New visitor messages don't reach a non-joined owner: the socket's
// visitor_message only fires once you've joined a conversation, and joining
// pauses the AI. So the list and the open transcript are re-read on a timer
// instead - this is the only way an ordinary widget reply shows up here.
const POLL_INTERVAL_MS = 10000;

// Matches the rows the list card can show without its own scrollbar taking
// over the column.
export const CONVERSATIONS_PER_PAGE = 10;

export default function Conversations() {
  const assistantId = useSelector((state) => state.assistants.activeId);
  const assistantName = useSelector(
    (state) =>
      state.assistants.list.find(
        (item) => item.id === state.assistants.activeId,
      )?.name || "Assistant",
  );
  const dispatch = useDispatch();

  // Deep link, for the "join this chat" mail the backend sends:
  //   /conversations?assistantId=<id>&conversationId=<id>[&join=1]
  // Everything is optional - without params this is the normal inbox.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedAssistantId = searchParams.get("assistantId") || "";
  const linkedConversationId = searchParams.get("conversationId") || "";
  const shouldAutoJoin = searchParams.get("join") === "1";

  const [conversations, setConversations] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  // Widget by default: this screen is the visitor inbox, and training chats
  // have their own page. "" is still every source, which the API gets by the
  // param being omitted.
  const [source, setSource] = useState("widget");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  // Details are fetched per conversation and cached by id, so re-selecting one
  // doesn't refetch it.
  const [details, setDetails] = useState({});
  const [ended, setEnded] = useState({});
  // The id whose detail request is in flight, so the pane can show a skeleton.
  const [loadingDetail, setLoadingDetail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // The link names the assistant, so the switcher and every other page follow
  // it rather than staying on whatever was last selected.
  useEffect(() => {
    if (linkedAssistantId && linkedAssistantId !== assistantId) {
      dispatch(setActiveAssistant(linkedAssistantId));
    }
  }, [linkedAssistantId, assistantId, dispatch]);

  const load = useCallback(async () => {
    if (!assistantId) return;

    setLoading(true);
    setError("");

    const result = await dispatch(
      listConversations(assistantId, {
        page,
        limit: CONVERSATIONS_PER_PAGE,
        ...(source ? { source } : {}),
      }),
    );

    setLoading(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't load your conversations.");
      setConversations([]);
      return;
    }

    const mapped = mapConversations(result.conversations, assistantName);
    setConversations(mapped);
    setPagination(result.pagination || null);
    setDetails({});
    // A linked conversation wins over the default first row. It is selected
    // even when it is not in this page of the list - the detail endpoints are
    // per id, so the pane can still load it.
    setSelectedId(linkedConversationId || mapped[0]?.id || null);
  }, [assistantId, assistantName, linkedConversationId, source, page, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // A quiet re-read: no spinner, and the selection and cached details survive,
  // unlike load() which resets both.
  const refresh = useCallback(async () => {
    if (!assistantId) return;

    const result = await dispatch(
      listConversations(assistantId, {
        page,
        limit: CONVERSATIONS_PER_PAGE,
        ...(source ? { source } : {}),
      }),
    );
    if (!result?.success) return;

    setConversations(mapConversations(result.conversations, assistantName));
    setPagination(result.pagination || null);

    if (!selectedId) return;

    const [record, transcript] = await Promise.all([
      dispatch(getConversationById(assistantId, selectedId)),
      dispatch(getConversationMessages(assistantId, selectedId)),
    ]);

    if (!record?.success) return;

    setDetails((prev) => ({
      ...prev,
      [selectedId]: mapConversation(
        record.conversation,
        assistantName,
        transcript?.messages,
      ),
    }));
  }, [assistantId, assistantName, selectedId, source, page, dispatch]);

  useEffect(() => {
    if (!assistantId) return undefined;

    let cancelled = false;
    let timer = null;

    // Chained rather than setInterval so a slow response can't stack calls,
    // and skipped entirely on a hidden tab - a background dashboard polling
    // two endpoints every 10s all day is pure waste.
    const tick = async () => {
      if (!document.hidden) await refresh();
      if (cancelled) return;
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [assistantId, refresh]);

  // The list rows carry a preview only. The record and its transcript are two
  // separate endpoints, so both are fetched and merged — reading messages off
  // the record alone left the thread permanently empty.
  useEffect(() => {
    if (!assistantId || !selectedId || details[selectedId]) return undefined;

    let cancelled = false;

    const load = async () => {
      setLoadingDetail(selectedId);

      const [record, transcript] = await Promise.all([
        dispatch(getConversationById(assistantId, selectedId)),
        dispatch(getConversationMessages(assistantId, selectedId)),
      ]);

      if (cancelled) return;

      setLoadingDetail("");

      if (!record?.success) return;

      setDetails((prev) => ({
        ...prev,
        [selectedId]: mapConversation(
          record.conversation,
          assistantName,
          transcript?.messages,
        ),
      }));
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [assistantId, selectedId, details, assistantName, dispatch]);

  // Statuses come from the data, so the dropdown matches the tabs.
  // One socket for the whole inbox: joins persist across row changes, and
  // switching rows only moves focus.
  const inbox = useOwnerInbox(assistantId, selectedId, {
    assistant: assistantName,
    visitor: "Visitor",
  });

  const statusOptions = useMemo(
    () => [
      "All",
      ...[...new Set(conversations.map((c) => c.status).filter(Boolean))].sort(),
    ],
    [conversations],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return conversations.filter((c) => {
      const matchesFilter = activeFilter === "All" || c.status === activeFilter;
      const matchesSearch =
        !query ||
        c.name.toLowerCase().includes(query) ||
        c.preview.toLowerCase().includes(query);
      return matchesFilter && matchesSearch;
    });
  }, [conversations, activeFilter, search]);

  const listedRow = filtered.find((c) => c.id === selectedId) || null;
  // Prefer the detailed record once it has arrived; the list row stands in
  // until then so the pane isn't blank. A deep-linked conversation may sit
  // outside the loaded page - or be filtered out by the search box - so its
  // fetched detail stands in for the missing row rather than silently
  // falling back to whatever happens to be first.
  const selected =
    (listedRow && details[listedRow.id]) ||
    listedRow ||
    (selectedId ? details[selectedId] : null) ||
    filtered[0] ||
    null;

  // Every conversation is addressable, so the URL is always the link to
  // share. Replaced rather than pushed: browsing the inbox shouldn't fill the
  // back button with one entry per row.
  const handleSelect = (id) => {
    setSelectedId(id);

    const next = new URLSearchParams(searchParams);
    next.set("conversationId", id);
    if (assistantId) next.set("assistantId", assistantId);
    // Opening another conversation shouldn't inherit the mail's auto-join.
    next.delete("join");
    setSearchParams(next, { replace: true });
  };

  const renderBody = () => {
    if (!assistantId) {
      return (
        <div className="asst-list-state">
          <span className="asst-list-empty-icon">
            <RobotIcon />
          </span>
          <strong>No assistant selected</strong>
          <p>Pick one from the switcher at the top of the sidebar.</p>
        </div>
      );
    }

    if (loading && !conversations.length) {
      return (
        <LoadingState title="Loading conversations" />
      );
    }

    if (error) {
      return (
        <div className="asst-list-state">
          <p className="auth-error">{error}</p>
          <button
            type="button"
            className="onboarding__btn onboarding__btn--primary"
            onClick={load}
          >
            Try again
          </button>
        </div>
      );
    }

    if (!conversations.length) {
      return (
        <div className="asst-list-state">
          <span className="asst-list-empty-icon">
            <RobotIcon />
          </span>
          <strong>No conversations yet</strong>
          <p>They&apos;ll appear here once visitors start chatting.</p>
        </div>
      );
    }

    return (
      <>
        <div className="conv-filter-row">
          <FilterTabs
            conversations={conversations}
            active={activeFilter}
            onChange={setActiveFilter}
          />
          {/* <SourceTabs
            active={source}
            onChange={(next) => {
              setSource(next);
              setPage(1);
            }}
          /> */}
        </div>

        <div className="conv-grid">
          <ConversationList
            conversations={filtered}
            liveIds={inbox.heldByAnyone}
            joinedIds={inbox.joined}
            unreadIds={inbox.unread}
            page={pagination?.page || page}
            totalPages={pagination?.totalPages || 1}
            total={pagination?.total || conversations.length}
            limit={pagination?.limit || CONVERSATIONS_PER_PAGE}
            loading={loading}
            onPageChange={setPage}
            selectedId={selected?.id}
            onSelect={handleSelect}
            search={search}
            onSearchChange={setSearch}
          />

          <ConversationDetail
            assistantId={assistantId}
            autoJoin={shouldAutoJoin && selected?.id === linkedConversationId}
            loading={loadingDetail === selected?.id}
            connected={inbox.connected}
            live={selected ? inbox.joined.has(selected.id) : false}
            joining={inbox.joining === selected?.id}
            liveMessages={selected ? inbox.threads[selected.id] || null : null}
            liveError={inbox.error}
            onJoin={inbox.join}
            onLeave={inbox.leave}
            onSend={inbox.send}
            conversation={
              selected && ended[selected.id]
                ? { ...selected, ended: true }
                : selected
            }
            onEndConversation={() =>
              selected &&
              setEnded((prev) => ({ ...prev, [selected.id]: true }))
            }
          />

          <LeadInfoPanel conversation={selected} />
        </div>
      </>
    );
  };

  return (
    <div className="dash">
      <Sidebar />

      <div className="dash-main">
        <Topbar
          title="Conversations"
          subtitle="View and manage all conversations with your assistant."
        >
          {/* <span className="cz-select-wrap conv-status-select">
            <select
              value={statusOptions.includes(activeFilter) ? activeFilter : "All"}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              {statusOptions.map((label) => (
                <option key={label} value={label}>
                  {label === "All" ? "All status" : label}
                </option>
              ))}
            </select>
            <ChevronDownIcon />
          </span>
          <button type="button" className="dash-topbar__share">
            <ExportIcon />
            Export
          </button> */}
        </Topbar>

        <div className="dash-content conv-content">{renderBody()}</div>
      </div>
    </div>
  );
}
